import get from 'lodash/get';
import Constants from '../../common/Constants';
import getConfig from '../Config/Config';

export default class Cookies {
    /**
     * Cookie static memory object
     */
    static memCookie = {};

    /**
     * This is kept to avoid object key length compare from load
     */
    static memCookieProcessed = false;

    /**
     * IF cookie object is not available in memory then
     * split the cookie and store it in key value pair
     */
    static init() {
        let localCookieObject = {};
        const documentCookie = document.cookie;
        /* istanbul ignore next */
        if (documentCookie.length > 0) {
            const cookiesArray = document.cookie.split(';');
            for (let i = 0; i < cookiesArray.length; i += 1) {
                try {
                    const cookie = decodeURIComponent(cookiesArray[i]);
                    const trimedOneCookie = cookie.trim();
                    const oneCookieObject = trimedOneCookie.split('=');
                    if (oneCookieObject.length === 2) {
                        const oneCookeName = oneCookieObject[0];
                        const oneCookeValue = oneCookieObject[1];
                        localCookieObject = { ...localCookieObject, [oneCookeName]: oneCookeValue };
                    }
                    // NOTE:
                    // as part of the bloomreach predictive search implementation, we have to access
                    // the pixel integrtation cookie named _br_uid_2 and the value is coming as
                    // uid%3D4516250946150%3Av%3D15.0%3Ats%3D1683828082235%3Ahc%3D66 when use decode the cookie value
                    // using the decodeURIComponent it is getting converted to "uid=4516250946150:v=15.0:ts=1683828082235:hc=66"
                    // as we splicting each cookie with '=' as delimiter. the oneCookieObject.length will be more than two
                    // because of that reason we are not able to read the _br_uid_2 cookie value.
                    // In order to solve the problem, added below if condition.
                    if (
                        (oneCookieObject?.[0] === '_br_uid_2' ||
                            oneCookieObject?.[0] === 'OptanonConsent') &&
                        oneCookieObject.length > 2
                    ) {
                        const [oneCookeName, ...oneCookeValues] = oneCookieObject;
                        const oneCookeValue = oneCookeValues.join('=');
                        localCookieObject = { ...localCookieObject, [oneCookeName]: oneCookeValue };
                    }
                } catch (error) {
                    console.log('decoding breadcrumb Cookie error', error);
                }
            }
        }
        Cookies.memCookieProcessed = true;
        Cookies.memCookie = localCookieObject;
        return Cookies.memCookie;
    }

    /**
     * take the cooke name and cross check with memory and
     * return the value
     */

    static load(name, readFromCookie = false) {
        let returnCookie;
        let decodedCookie;
        /* istanbul ignore next */
        if (typeof document !== 'undefined') {
            if (!readFromCookie && Cookies.memCookieProcessed) {
                returnCookie = Cookies.memCookie[name];
            } else {
                const tempCookie = Cookies.init() || {};
                returnCookie = tempCookie[name];
            }
        }
        try {
            if (returnCookie) {
                decodedCookie = decodeURIComponent(returnCookie);
            } else if (readFromCookie) {
                const CookieValue = Cookies.init() || {};
                returnCookie = CookieValue[name];
                decodedCookie = returnCookie && decodeURIComponent(returnCookie);
            }
        } catch (error) {
            console.log('decoding breadcrumb Cookie error', error);
        }
        return decodedCookie;
    }

    /**
     * CreateCookie functions takes the name for the cookie and the value
     * and the expiry date
     * and sets the cookie
     */
    static save(name, value, daysParam, domain = '.jcpenney.com', isSecure = true) {
        /**
         * Condition for the server side rendering
         */

        if (__SERVER__) {
            return;
        }
        let tempCookie = {};
        /* istanbul ignore next */
        if (!Cookies.memCookieProcessed) {
            tempCookie = Cookies.init() || {};
        } else {
            tempCookie = Cookies.memCookie;
        }
        let expires;
        const date = new Date();
        let days = daysParam;
        const cookieSaveConfig = getConfig('context.preferences.cookieSave', {});
        if (
            get(cookieSaveConfig, 'persistedList', [
                'DP_USER_STATE',
                'Access_Token',
                'ACCOUNT_ID',
            ]).includes(name)
        ) {
            days = get(cookieSaveConfig, 'expiryDays', 730);
        }
        if (days) {
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = `; expires=${date.toGMTString()}`;
        } else {
            expires = '';
        }
        let domainQuery = `domain=${domain};`;
        if (name === Constants.ACCESS_TOKEN || name === Constants.REFRESH_TOKEN) {
            domainQuery = '';

            // remove old cookie with .jcpenney.com domain
            Cookies.remove(name, '.jcpenney.com');
        }
        let secure = '';
        if (isSecure) {
            secure = 'secure';
        }

        document.cookie = `${name}=${value}${expires};${domainQuery}path=/;${secure}`;
        Cookies.memCookie = { ...tempCookie, [name]: value };
    }

    /**
     * Remove a cookie given a name
     */
    static remove(name, domain = '.jcpenney.com', isNativeApp = false) {
        /**
         * Condition for the server side rendering
         */
        if (__SERVER__) {
            return;
        }

        if (!name) {
            return;
        }
        let tempCookie = {};
        /* istanbul ignore next */
        if (!Cookies.memCookieProcessed) {
            tempCookie = Cookies.init() || {};
        } else {
            tempCookie = Cookies.memCookie;
        }

        let domainQuery = isNativeApp ? `domain=;` : `domain=${domain};`;
        if (name === Constants.ACCESS_TOKEN || name === Constants.REFRESH_TOKEN) {
            domainQuery = '';

            // force remove old cookie with .jcpenney.com domain
            const expires = '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;domain=.jcpenney.com;path=/';
            document.cookie = name + expires;
        }

        const expires = `=;expires=Thu, 01 Jan 1970 00:00:01 GMT;${domainQuery}path=/`;
        document.cookie = name + expires;
        const newCookieInst = { ...tempCookie };
        delete newCookieInst[name];
        Cookies.memCookie = { ...newCookieInst };
    }

    /**
     * Remove all cookies fuction from fusion team
     */
    static clearAll(domain) {
        if (__SERVER__) {
            return false;
        }

        try {
            const cookies = document.cookie.split(';');
            const d = (domain || window.location.hostname).split('.');
            for (let c = 0; c < cookies.length; c += 1) {
                const domainSlice = d.slice(0); // Clone
                while (domainSlice.length > 1) {
                    const cookieBase = `${encodeURIComponent(
                        cookies[c].split(';')[0].split('=')[0].trim()
                    )}=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=.${domainSlice.join(
                        '.'
                    )} ;path=`;
                    const p = location.pathname.split('/');
                    document.cookie = `${cookieBase}/`;
                    while (p.length > 0) {
                        document.cookie = cookieBase + p.join('/');
                        p.pop();
                    }
                    domainSlice.shift(); // Pops first item of the array
                }
            }
            return true;
        } catch (e) {
            return false;
        }
    }
}
